import React, { useState, useContext, useRef, useLayoutEffect, useEffect, useMemo } from 'react';
import { useLocation, navigate } from '@reach/router';
import List from '@mui/material/List';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import { ReactComponent as IconSalesrook } from '@assets/svg/salesrook.svg';

import { menuConfig, MenuItem, MenuGroup } from './partials';
import StyledSideNav from './sideNav.style';
import { ApplicationContext, canOpenPage } from '@particles';
import { useMyAccess } from '@api';
import { signOutEverywhere } from '../../firebase/session';

interface SideNavProps {
  isSuperAdmin: boolean;
  /** Mobile overlay open state. When undefined the drawer is desktop-only. */
  mobileOpen?: boolean;
  /** Called when the mobile overlay drawer requests to close (backdrop click, nav click, escape). */
  onMobileClose?: () => void;
}

const GOLD = '#f0cf69';
const INDICATOR_EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';
const COLLAPSED_GROUPS_LS_KEY = 'sidenav.collapsedGroups.v1';

const loadCollapsedGroups = (): Set<string> => {
  try {
    const raw = localStorage.getItem(COLLAPSED_GROUPS_LS_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed.filter((x) => typeof x === 'string')) : new Set();
  } catch {
    return new Set();
  }
};

const persistCollapsedGroups = (set: Set<string>): void => {
  try {
    localStorage.setItem(COLLAPSED_GROUPS_LS_KEY, JSON.stringify(Array.from(set)));
  } catch {
    /* ignore quota / privacy-mode errors */
  }
};

export const SideNav: React.FC<SideNavProps> = ({
  isSuperAdmin,
  mobileOpen,
  onMobileClose
}: SideNavProps) => {
  const {
    state: { user },
    dispatch
  } = useContext(ApplicationContext);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Mobile always renders in the "expanded" layout — the whole drawer is an
  // overlay so there is no rail state to toggle.
  const [isCollapsed, setIsCollapsed] = useState(true);
  const expanded = isMobile ? true : isCollapsed;
  const [expandedItems, setExpandedItems] = useState<{ [key: string]: boolean }>({});
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => loadCollapsedGroups());
  const [indicator, setIndicator] = useState({ top: 0, height: 0, visible: false });

  const location = useLocation();
  const { pathname } = location;

  const listRef = useRef<HTMLDivElement | null>(null);
  const activeRowRef = useRef<HTMLDivElement | null>(null);

  // Staff on a custom role see only their sections (`canOpenPage`); a Super
  // Admin sees everything. The server enforces the same grants on every call.
  const { data: access } = useMyAccess(user?.id, isSuperAdmin);
  const groups: MenuGroup[] = useMemo(() => {
    if (!isSuperAdmin) return menuConfig.menuConfigAdmin;
    if (access?.role === 'Super Admin') return menuConfig.menuConfigSuperAdmin;
    const visible = (item: MenuItem) => !item.to || canOpenPage(item.to, access);
    return menuConfig.menuConfigSuperAdmin
      .map((group) => ({
        ...group,
        items: group.items
          .map((item) => ({ ...item, subItems: item.subItems?.filter(visible) }))
          .filter(
            (item) => visible(item) && (!item.subItems || item.subItems.length > 0 || item.to)
          )
      }))
      .filter((group) => group.items.length > 0);
  }, [isSuperAdmin, access]);

  /** Lookup: route path → owning group label (undefined for unlabelled top groups). */
  const routeToGroupLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of groups) {
      if (!g.label) continue;
      for (const item of g.items) {
        if (item.to) map.set(item.to, g.label);
        for (const sub of item.subItems ?? []) {
          if (sub.to) map.set(sub.to, g.label);
        }
      }
    }
    return map;
  }, [groups]);

  /** If the active route lives inside a currently-collapsed group, auto-expand
      that group so the row (and the sliding indicator) becomes visible. */
  useEffect(() => {
    const owner = routeToGroupLabel.get(pathname);
    if (!owner) return;
    setCollapsedGroups((prev) => {
      if (!prev.has(owner)) return prev;
      const next = new Set(prev);
      next.delete(owner);
      persistCollapsedGroups(next);
      return next;
    });
  }, [pathname, routeToGroupLabel]);

  const toggleGroupCollapsed = (label: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      persistCollapsedGroups(next);
      return next;
    });
  };

  useLayoutEffect(() => {
    if (activeRowRef.current && listRef.current) {
      const rowRect = activeRowRef.current.getBoundingClientRect();
      const listRect = listRef.current.getBoundingClientRect();
      setIndicator({
        top: rowRect.top - listRect.top + listRef.current.scrollTop,
        height: rowRect.height,
        visible: true
      });
    } else {
      setIndicator((prev) => ({ ...prev, visible: false }));
    }
  }, [pathname, expanded, expandedItems, isSuperAdmin, collapsedGroups]);

  const handleToggle = () => {
    if (isMobile) {
      onMobileClose?.();
      return;
    }
    setIsCollapsed(!isCollapsed);
  };

  const handleExpandClick = (label: string) => {
    setExpandedItems((prev) => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  const renderListItem = (item: MenuItem, itemIndex: number, isSubItem = false) => {
    const { label, Icon, cssClass, to, subItems } = item;

    const isSelected = to === pathname;
    const isExpanded = expandedItems[label] || false;
    const hasSubItems = subItems && subItems.length > 0;
    const isNavigableLink = !!to && to !== '/error-403' && !hasSubItems;
    const className = [cssClass, isSelected && `${cssClass}-selected`].filter(Boolean).join(` `);

    const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }
      e.preventDefault();
      navigate(to as string);
      if (isMobile) onMobileClose?.();
    };

    const handleButtonClick = () => {
      if (hasSubItems) {
        handleExpandClick(label);
      } else if (to === '/error-403') {
        signOutEverywhere();
        dispatch({ key: 'user', value: undefined, type: 'set' });
        // A company Admin signs in again on the dashboard's own login page.
        navigate(isSuperAdmin ? '/login' : '/admin/login');
      }
    };

    const commonSx = isSubItem ? { pl: 4, textDecoration: 'none' } : { textDecoration: 'none' };

    return (
      <Box
        key={itemIndex}
        ref={isSelected ? (activeRowRef as React.RefObject<HTMLDivElement>) : undefined}
      >
        {isNavigableLink ? (
          <ListItem
            component="a"
            href={to}
            selected={isSelected}
            className={className}
            sx={commonSx}
            onClick={handleLinkClick}
          >
            <ListItemIcon>
              <Icon />
            </ListItemIcon>
            <ListItemText
              primary={label}
              primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
            />
          </ListItem>
        ) : (
          <>
            <ListItem
              button
              selected={isSelected}
              className={className}
              sx={commonSx}
              onClick={handleButtonClick}
            >
              <ListItemIcon>
                <Icon />
              </ListItemIcon>
              <ListItemText
                primary={label}
                primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
              />
              {hasSubItems && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
            </ListItem>
            {hasSubItems && (
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {subItems.map((subItem, subIndex) => renderListItem(subItem, subIndex, true))}
                </List>
              </Collapse>
            )}
          </>
        )}
      </Box>
    );
  };

  const renderGroupHeader = (label: string, groupCollapsed: boolean) => {
    // Narrow sidebar → labels are hidden anyway; show a divider between groups.
    if (!expanded) {
      return (
        <Divider
          sx={{
            my: 1,
            mx: 1,
            borderColor: 'rgba(240, 207, 105, 0.25)'
          }}
        />
      );
    }

    return (
      <Box
        onClick={() => toggleGroupCollapsed(label)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleGroupCollapsed(label);
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          userSelect: 'none',
          mx: 1,
          px: 1.25,
          pt: 1.25,
          pb: 0.5,
          borderRadius: 1,
          transition: 'background-color 200ms ease',
          '&:hover': { backgroundColor: 'rgba(240, 207, 105, 0.08)' },
          '&:focus-visible': { outline: `2px solid ${GOLD}`, outlineOffset: -2 }
        }}
      >
        <Typography
          variant="overline"
          sx={{
            flex: 1,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.12rem',
            color: 'rgba(255, 255, 255, 0.5)',
            lineHeight: 1.4
          }}
        >
          {label}
        </Typography>
        <ExpandMore
          sx={{
            color: 'rgba(255, 255, 255, 0.5)',
            fontSize: 18,
            transition: 'transform 220ms ease',
            transform: groupCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)'
          }}
        />
      </Box>
    );
  };

  const renderGroup = (group: MenuGroup, groupIndex: number) => {
    const groupCollapsed = !!group.label && collapsedGroups.has(group.label);
    // Narrow sidebar always shows items (no headers to click for collapse).
    const showItems = !expanded || !groupCollapsed;
    return (
      <Box key={groupIndex} sx={{ mb: 0.5 }}>
        {group.label && renderGroupHeader(group.label, groupCollapsed)}
        <Collapse in={showItems} timeout="auto" unmountOnExit={false}>
          <Box>{group.items.map((item, itemIndex) => renderListItem(item, itemIndex))}</Box>
        </Collapse>
      </Box>
    );
  };

  const renderMenu = () => groups.map((group, groupIndex) => renderGroup(group, groupIndex));

  return (
    <StyledSideNav
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? !!mobileOpen : true}
      onClose={isMobile ? onMobileClose : undefined}
      ModalProps={{ keepMounted: true }}
      $open={isCollapsed}
      $mobile={isMobile}
    >
      <div style={{ display: 'flex', alignSelf: !expanded ? 'center' : 'auto' }}>
        {expanded && <IconSalesrook width={150} height={60} />}

        {expanded && <Box flex={1} />}
        <IconButton onClick={handleToggle}>
          <MenuIcon sx={{ color: 'white' }} />
        </IconButton>
      </div>
      <List
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <Box
          ref={listRef}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            position: 'relative',
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(255,255,255,0.25) transparent',
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255,255,255,0.2)',
              borderRadius: 3
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'rgba(255,255,255,0.35)'
            }
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              left: 0,
              right: 8,
              top: indicator.top,
              height: indicator.height,
              background: 'rgba(240, 207, 105, 0.15)',
              borderLeft: `3px solid ${GOLD}`,
              borderRadius: '0 8px 8px 0',
              boxShadow: '0 2px 8px rgba(240, 207, 105, 0.15)',
              opacity: indicator.visible ? 1 : 0,
              transition: `top 320ms ${INDICATOR_EASE}, height 320ms ${INDICATOR_EASE}, opacity 220ms ease`,
              pointerEvents: 'none',
              zIndex: 0
            }}
          />
          {renderMenu()}
        </Box>
      </List>
    </StyledSideNav>
  );
};

export default SideNav;
